import moment from 'moment';
import {
  VA_API, STRUCTURES_API,
} from '@/api';
import dateStore from '@/utils/dateStore';
import { createMutations } from '@/utils/vuexHelper';
import VABaseStore from '../VABaseStore';

const constValues = {
  defautNotificationOptions: {
    displayTime: 4000,
    animation: {
      show: {
        type: 'fade', duration: 400, from: 0, to: 1,
      },
      hide: { type: 'fade', duration: 40, to: 0 },
    },
  },
};
const state = {
  selectedDate: dateStore.getDefaultDate().toISOString(),
  lmpTable: {
    seriesApiParam: '',
    series: [
      { columnName: 'RTM LMP', name: 'ciso_oasisprc_rtm_en_lmp' },
      { columnName: 'FMM LMP', name: 'ciso_oasisprc_fmm_en_lmp' },
    ],
    data: [],
  },
  meterDeviationTable: {
    seriesApiParam: '',
    series: [{ name: 'calc_ciso_deviation_qty' }],
    data: [],
  },
  pnlTable: {
    seriesApiParam: '',
    series: [{ name: 'calc_ciso_pnl' }],
    data: [],
  },
  testAndAlertsTable: {
    seriesApiParam: '',
    testAndAlerts: {
      'Transmission Feasibility Test': {
        series: [
          { name: 'baa_transmission_feasibility_test_failure_t1d' },
          { name: 'baa_transmission_feasibility_test_failure_t40' },
          { name: 'baa_transmission_feasibility_test_failure_t55' },
          { name: 'baa_transmission_feasibility_test_failure_t75' },
        ],
        nonPositiveValue: 'PASS',
        type: 'test',
        positiveValue: 'FAIL',
      },
      'Balancing Test': {
        series: [
          { name: 'baa_balancing_test_failure_t40' },
          { name: 'baa_balancing_test_failure_t55' },
          { name: 'baa_balancing_test_failure_t75' },
        ],
        nonPositiveValue: 'PASS',
        type: 'test',
        positiveValue: 'FAIL',
      },
      'WEIM RSE Capacity Test': {
        series: [
          { name: 'ciso_oasis_weim_rse_capacity_test_t40min_down_test_failure' },
          { name: 'ciso_oasis_weim_rse_capacity_test_t40min_up_test_failure' },
          { name: 'ciso_oasis_weim_rse_capacity_test_t55min_down_test_failure' },
          { name: 'ciso_oasis_weim_rse_capacity_test_t55min_up_test_failure' },
          { name: 'ciso_oasis_weim_rse_capacity_test_t75min_down_test_failure' },
          { name: 'ciso_oasis_weim_rse_capacity_test_t75min_up_test_failure' },
        ],
        nonPositiveValue: 'PASS',
        type: 'test',
        positiveValue: 'FAIL',
      },

      'WEIM Flex Ramp Test': {
        series: [
          { name: 'ciso_oasis_ene_eim_flex_ramp_input_t40min_up_test_failure' },
          { name: 'ciso_oasis_ene_eim_flex_ramp_input_t55min_up_test_failure' },
          { name: 'ciso_oasis_ene_eim_flex_ramp_input_t75min_up_test_failure' },
          { name: 'ciso_oasis_ene_eim_flex_ramp_input_t40min_down_test_failure' },
          { name: 'ciso_oasis_ene_eim_flex_ramp_input_t55min_down_test_failure' },
          { name: 'ciso_oasis_ene_eim_flex_ramp_input_t75min_down_test_failure' },
        ],
        nonPositiveValue: 'PASS',
        type: 'test',
        positiveValue: 'FAIL',
      },
      // 'VER Curtailment': {
      //  series: [
      //    { name: 'calc_ciso_ver_curt_qty', testOrAlertName: 'VER Curtailment' },
      //  ],
      //  nonPositiveValue: 'NONE',
      //  positiveValue: 'YES',
      // },
    },
    data: [],
  },
};

const getters = {
};

const actions = {
  async initialize({ commit, dispatch }) {
    dispatch('resetSelectedState');
    await dispatch('configureSeriesIds');

    commit('setSelectedDate', dateStore.getDefaultDate().toISOString());
    await dispatch('fetchVariants', state.selectedDate);
  },
  async configureSeriesIds({ commit, dispatch }) {
    // fetch series
    let seriesResponse;
    try {
      seriesResponse = await STRUCTURES_API.get('series');
    } catch (e) {
      dispatch('showErrorMessage', 'Unable to fetch series information');
    }
    const series = seriesResponse.data.data;

    // configure series
    try {
      commit('setLmpTableSeriesIds', series);
      commit('setMeterDeviationTableSeriesIds', series);
      commit('setPnlTableSeriesIds', series);
      commit('setTestAndAlertsTableSeriesIds', series);
    } catch (e) {
      dispatch('showErrorMessage', 'Error occurred when configuring series');
    }
  },
  async fetchMeterDeviationTableData({ commit, state, dispatch }) {
    let data;
    // ----------------------------fetch data--------------------------------
    try {
      const params = {
        startTime: moment(state.selectedDate).toISOString(),
        endTime: moment(state.selectedDate).add(1, 'day').toISOString(),
        variant: state.VABaseStore.selectedVariant,
        aggregateType: 'abs_sum',
        objectReferences: state.meterDeviationTable.seriesApiParam,
      };
      data = (await VA_API.get('intervals/aggregate/location', { params })).data.data;
    } catch (err) {
      dispatch('showErrorMessage', 'Unable to fetch Meter Deviation Data');
      console.log(err);
    }
    if (!data) {
      return;
    }

    // ---------------------------- format data -----------------------------
    data = data.map((d) => ({
      locationName: d.locationName,
      value: d.value,
    }));
    // sort by highest value
    data.sort((a, b) => {
      const valueDiff = b.value - a.value;
      return valueDiff === 0 ? a.locationName.localeCompare(b.locationName) : valueDiff;
    });
    // add row numbers based on order
    data.forEach((d, index) => { d.rowNum = index + 1; });
    commit('setMeterDeviationTableData', Object.values(data));
  },
  async fetchPnlTableData({ commit, state, dispatch }) {
    // ---------------------------- fetch data -----------------------------
    let data;
    try {
      const params = {
        startTime: moment(state.selectedDate).toISOString(),
        endTime: moment(state.selectedDate).add(1, 'day').toISOString(),
        variant: state.VABaseStore.selectedVariant,
        aggregateType: 'sum',
        objectReferences: state.pnlTable.seriesApiParam,
      };
      data = (await VA_API.get('intervals/aggregate/location', { params })).data.data;
    } catch (err) {
      dispatch('showErrorMessage', 'Unable to fetch PnL Data');
      console.log(err);
    }

    if (!data) {
      return;
    }
    // ---------------------------- format data -----------------------------
    data = data.map((d) => ({
      locationName: d.locationName,
      pnlType: d.value > 0 ? 'Benefit' : 'Loss',
      value: d.value,
    }));
    // sort by highest value
    data.sort((a, b) => {
      const valueDiff = b.value - a.value;
      return valueDiff === 0 ? a.locationName.localeCompare(b.locationName) : valueDiff;
    });
    commit('setPnlTableData', Object.values(data));
  },
  async fetchLmpTableData({ commit, state, dispatch }) {
    let responseMinData; let
      responseMaxData;
    // ---------------------------- fetch data -----------------------------
    try {
      const params = {
        startTime: moment(state.selectedDate).toISOString(),
        endTime: moment(state.selectedDate).add(1, 'day').toISOString(),
        variant: state.VABaseStore.selectedVariant,
        aggregateType: 'min',
        objectReferences: state.lmpTable.seriesApiParam,
      };
      responseMinData = (await VA_API.get('intervals/aggregate/location-group', { params })).data.data;

      params.aggregateType = 'max';
      responseMaxData = (await VA_API.get('intervals/aggregate/location-group', { params })).data.data;
    } catch (err) {
      dispatch('showErrorMessage', 'Unable to fetch LMP Data');
      console.log(err);
    }

    if (!responseMaxData || !responseMinData) {
      return;
    }
    // ---------------------------------format data------------------------------------

    const data = {};
    responseMinData.forEach((minData) => {
      const dataPointName = state.lmpTable.series.find((series) => series.id === minData.objectReference);
      if (dataPointName !== undefined) {
        data[`${minData.locationGroupName}|${minData.objectReference}`] = {
          locationGroupName: minData.locationGroupName,
          minLocation: minData.locationName,
          minTime: moment(minData.startTime).format('HH:mm'),
          minVal: minData.value,
          dataPointName: dataPointName.columnName,
        };
      }
    });
    responseMaxData.forEach((maxData) => {
      const key = `${maxData.locationGroupName}|${maxData.objectReference}`;
      if (data[key] !== undefined) {
        data[key].maxLocation = maxData.locationName;
        data[key].maxTime = moment(maxData.startTime).format('HH:mm');
        data[key].maxVal = maxData.value;
      }
    });
    commit('setLmpTableData', Object.values(data));
  },
  async fetchTestAndAlertsTableData({ commit, state, dispatch }) {
    // ----------------------------- fetch data ------------------------------
    let data;
    try {
      const params = {
        startTime: moment(state.selectedDate).toISOString(),
        endTime: moment(state.selectedDate).add(1, 'day').toISOString(),
        variant: state.VABaseStore.selectedVariant,
        aggregateType: 'max',
        objectReferences: state.testAndAlertsTable.seriesApiParam,
      };
      data = (await VA_API.get('intervals/aggregate/all', { params })).data.data;
    } catch (err) {
      dispatch('showErrorMessage', 'Unable to fetch Test and Alerts Data');
      console.log(err);
    }

    if (!data) {
      return;
    }
    // ----------------------------- format data -------------------------------
    const tableData = [];
    Object.entries(state.testAndAlertsTable.testAndAlerts).forEach(([testOrAlertName, testOrAlert]) => {
      let result = testOrAlert.nonPositiveValue;
      let anyData = false;
      // try to find any series with a max greater than 0
      testOrAlert.series.forEach((series) => {
        const d = data.find((d1) => d1.objectReference === series.id);
        if (d !== undefined) {
          anyData = true;
          result = d.value > 0 ? testOrAlert.positiveValue : result;
        }
      });
      // add result of each test or alert
      tableData.push({
        dataPointName: testOrAlertName,
        type: testOrAlert.type,
        value: result,
        anyData,
      });
    });
    // remove any tests or alerts with no data
    commit('setTestAndAlertsTableData', tableData.filter((d) => d.anyData));
  },
  resetSelectedState({ commit, state }) {
    commit('resetState');
  },
  showErrorMessage(v, e) {
    this.$notify({
      message: e,
      type: 'Error',
      ...(constValues.defaultNotificationAnimation),
    }, {
      position: 'bottom center',
      direction: 'up-stack',
    });
  },
};

const mutations = {
  resetState(state) {
    state.lmpTable.data = [];
    state.meterDeviationTable.data = [];
    state.pnlTable.data = [];
    state.testAndAlertsTable.data = [];
  },
  setLmpTableSeriesIds(state, series) {
    state.lmpTable.series.forEach((s1) => {
      s1.id = series.find((s2) => s1.name === s2.name).id;
    });
    state.lmpTable.seriesApiParam = state.lmpTable.series.map((s) => s.id).join(',');
  },
  setMeterDeviationTableSeriesIds(state, series) {
    state.meterDeviationTable.series.forEach((s1) => {
      s1.id = series.find((s2) => s1.name === s2.name).id;
    });
    state.meterDeviationTable.seriesApiParam = state.meterDeviationTable.series.map((s) => s.id).join(',');
  },
  setPnlTableSeriesIds(state, series) {
    state.pnlTable.series.forEach((s1) => {
      s1.id = series.find((s2) => s1.name === s2.name).id;
    });
    state.pnlTable.seriesApiParam = state.pnlTable.series.map((s) => s.id).join(',');
  },
  setTestAndAlertsTableSeriesIds(state, series) {
    state.testAndAlertsTable.seriesApiParam = Object.keys(state.testAndAlertsTable.testAndAlerts)
      .reduce((seriesApiParamAccum, testOrAlertName) => {
        // for each series that contribute to test or alert find the series id
        state.testAndAlertsTable.testAndAlerts[testOrAlertName].series.forEach((s1) => {
          s1.id = series.find((s2) => s1.name === s2.name).id;
        });
        // for each series list add to the seriesApiParam val
        return seriesApiParamAccum.concat(
          seriesApiParamAccum === '' ? '' : ',',
          state.testAndAlertsTable.testAndAlerts[testOrAlertName].series.map((series) => series.id).join(','));
      }, '');
  },
  setLmpTableData(state, data) {
    state.lmpTable.data = data;
  },
  setMeterDeviationTableData(state, data) {
    state.meterDeviationTable.data = data;
  },
  setPnlTableData(state, data) {
    state.pnlTable.data = data;
  },
  setTestAndAlertsTableData(state, data) {
    state.testAndAlertsTable.data = data;
  },
  ...createMutations(
    'selectedDate',
  ),

};

export default {
  namespaced: true,
  modules: { VABaseStore },
  state,
  getters,
  actions,
  mutations,
};